import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { purchaseRequisitionsApi, vendorsApi, warehousesApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M1-PROC-01 — Purchase requisition detail with state actions (Submit/Approve/Reject)
 * and a Convert dialog that picks vendor + warehouse + per-line rate. On success,
 * navigates to the new draft PurchaseOrder.
 */
export function PurchaseRequisitionDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canCreate  = useAuth(s => s.hasPermission("procurement.requisition.create"));
  const canApprove = useAuth(s => s.hasPermission("procurement.requisition.approve"));
  const canConvert = useAuth(s => s.hasPermission("procurement.po.create"));

  const [openAction, setOpenAction] = useState<null | "submit" | "approve" | "reject" | "convert">(null);
  const [reason, setReason] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [rateByLine, setRateByLine] = useState<Record<string, number>>({});

  const query = useQuery({
    queryKey: ["purchase-requisition", id],
    queryFn: () => purchaseRequisitionsApi.get(id),
    enabled: !!id,
  });
  const vendors = useQuery({
    queryKey: ["vendors", "all"],
    queryFn: () => vendorsApi.list({ pageSize: 200, isActive: true }),
    enabled: openAction === "convert",
  });
  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
    enabled: openAction === "convert",
  });

  const stateMutation = useMutation({
    mutationFn: async () => {
      if (!query.data) return;
      const rv = query.data.rowVersion;
      switch (openAction) {
        case "submit":  await purchaseRequisitionsApi.submit(id, rv); break;
        case "approve": await purchaseRequisitionsApi.approve(id, rv); break;
        case "reject":  await purchaseRequisitionsApi.reject(id, reason, rv); break;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-requisition", id] });
      qc.invalidateQueries({ queryKey: ["purchase-requisitions"] });
      setOpenAction(null); setReason("");
    },
  });

  const convertMutation = useMutation({
    mutationFn: () => purchaseRequisitionsApi.convertToPo(id, {
      vendorId, warehouseId,
      expectedDeliveryDate: expectedDeliveryDate || undefined,
      rateByLine,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["purchase-requisition", id] });
      navigate(`/procurement/purchase-orders/${r.purchaseOrderId}`);
    },
  });

  if (query.isLoading || !query.data) return <CircularProgress />;
  const r = query.data;
  const allLinesHaveRate = r.lines.every(l => (rateByLine[l.id] ?? 0) > 0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{r.documentNo}</Typography>
          <Chip label={r.status} color={STATUS_COLOR[r.status] ?? "default"} size="small" />
          {r.convertedToPurchaseOrderId && (
            <Chip
              size="small" color="success"
              label="Converted →"
              component={RouterLink as any}
              to={`/procurement/purchase-orders/${r.convertedToPurchaseOrderId}`}
              clickable
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          {r.status === "Draft" && canCreate && (
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => setOpenAction("submit")}>Submit</Button>
          )}
          {r.status === "Submitted" && canApprove && (
            <>
              <Button variant="contained" color="success" startIcon={<CheckIcon />}
                onClick={() => setOpenAction("approve")}>Approve</Button>
              <Button color="warning" startIcon={<CancelIcon />}
                onClick={() => setOpenAction("reject")}>Reject</Button>
            </>
          )}
          {r.status === "Approved" && !r.convertedToPurchaseOrderId && canConvert && (
            <Button variant="contained" startIcon={<SwapHorizIcon />}
              onClick={() => setOpenAction("convert")}>Convert → Purchase Order</Button>
          )}
        </Stack>
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack><Typography variant="caption" color="text.secondary">Document date</Typography>
            <Typography>{new Date(r.documentDate).toLocaleDateString()}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Needed by</Typography>
            <Typography>{r.neededByDate ? new Date(r.neededByDate).toLocaleDateString() : "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Lines</Typography>
            <Typography>{r.lineCount}</Typography></Stack>
        </Stack>
        {r.reason && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Reason</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{r.reason}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right">Requested qty</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {r.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                </TableCell>
                <TableCell align="right">{l.requestedQty}</TableCell>
                <TableCell>{l.reason ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Button onClick={() => navigate("/procurement/purchase-requisitions")}>Back to list</Button>

      {/* Simple confirm/reject dialog */}
      <Dialog open={openAction !== null && openAction !== "convert"}
        onClose={() => { setOpenAction(null); setReason(""); }} maxWidth="xs" fullWidth>
        <DialogTitle>
          {openAction === "submit" && "Submit indent"}
          {openAction === "approve" && "Approve indent"}
          {openAction === "reject" && "Reject indent"}
        </DialogTitle>
        <DialogContent>
          {openAction === "reject" && (
            <TextField label="Reason *" fullWidth multiline minRows={3} sx={{ mt: 1 }}
              value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          {(openAction === "submit" || openAction === "approve") && (
            <DialogContentText>Confirm the state transition.</DialogContentText>
          )}
          {stateMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(stateMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAction(null); setReason(""); }}>Cancel</Button>
          <Button variant="contained"
            disabled={(openAction === "reject" && !reason) || stateMutation.isPending}
            onClick={() => stateMutation.mutate()}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={openAction === "convert"} onClose={() => setOpenAction(null)} maxWidth="md" fullWidth>
        <DialogTitle>Convert indent → Purchase Order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pick the vendor, the receiving warehouse and a unit rate per line; the system creates a
            Draft Purchase Order with these lines linked back to the indent.
          </DialogContentText>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <TextField select label="Vendor *" sx={{ minWidth: 240 }}
              value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <MenuItem value="">— pick vendor —</MenuItem>
              {vendors.data?.items.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Receiving warehouse *" sx={{ minWidth: 240 }}
              value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <MenuItem value="">— pick warehouse —</MenuItem>
              {warehouses.data?.items.map(w => (
                <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
              ))}
            </TextField>
            <TextField type="date" label="Expected delivery" InputLabelProps={{ shrink: true }}
              value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)} />
          </Stack>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>Rate *</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {r.lines.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{l.itemName} <Typography variant="caption" color="text.secondary">{l.sku}</Typography></TableCell>
                  <TableCell align="right">{l.requestedQty}</TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 140 }}
                      value={rateByLine[l.id] ?? ""}
                      onChange={(e) => setRateByLine(prev => ({ ...prev, [l.id]: Number(e.target.value) || 0 }))}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {convertMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(convertMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAction(null)}>Cancel</Button>
          <Button variant="contained"
            disabled={!vendorId || !warehouseId || !allLinesHaveRate || convertMutation.isPending}
            onClick={() => convertMutation.mutate()}>Convert</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
